import React, { useState } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import './PlayerLookup.css';

function PlayerLookup({ onLookupVehicle, onLookupPhone }) {
  const { apiFetch } = useAuth();
  const { language } = useI18n();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [expandedIdentifier, setExpandedIdentifier] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const t = language === 'da'
    ? {
        failedSearch: 'Kunne ikke søge spiller',
        failedCharacters: 'Kunne ikke hente karakterer',
        failedDetails: 'Kunne ikke hente karakterdetaljer',
        noCharactersFound: 'Ingen karakterer fundet for denne spiller',
        title: 'Spiller opslag',
        subtitle: 'søg via identifikator, fornavn eller efternavn',
        placeholder: 'Indtast identifikator eller navn...',
        searching: 'søger...',
        search: 'søg',
        foundPlayers: 'Fandt {count} spiller(e)',
        hideDetails: 'Skjul detaljer',
        viewDetails: 'Vis detaljer',
        job: 'Job:',
        group: 'Gruppe:',
        bank: 'Bank:',
        cash: 'Kontant:',
        phoneNumbers: 'Telefon:',
        itemCash: 'Kontanter i items:',
        blackMoney: 'Sorte penge:',
        loadingDetails: 'Indlæser detaljer...',
        characters: 'Karakterer ({count} total)',
        inventory: 'Inventory ({count} items)',
        noItems: 'Ingen items i inventory',
        ownedVehicles: 'Ejede køretøjer ({count})',
        unknownVehicle: 'Ukendt køretøj',
        noPlate: 'INGEN PLADE',
        stored: 'Lagret',
        out: 'Ude',
        noVehicles: 'Ingen ejede køretøjer',
        lookupVehicle: 'Slå op i køretøj opslag',
        characterDetails: 'Karakterdetaljer',
        characterSlot: 'Karakterslot:',
        dob: 'fødselsdato:',
        gender: 'Koen:',
        male: 'Mand',
        female: 'Kvinde',
        height: 'højde:',
        na: 'N/A',
        noResults: 'Ingen spillere fundet, der matcher "{search}"'
      }
    : {
        failedSearch: 'Failed to search player',
        failedCharacters: 'Failed to fetch characters',
        failedDetails: 'Failed to fetch character details',
        noCharactersFound: 'No characters found for this player',
        title: 'Player Lookup',
        subtitle: 'Search by identifier, first name, or last name',
        placeholder: 'Enter identifier or name...',
        searching: 'Searching...',
        search: 'Search',
        foundPlayers: 'Found {count} player(s)',
        hideDetails: 'Hide Details',
        viewDetails: 'View Details',
        job: 'Job:',
        group: 'Group:',
        bank: 'Bank:',
        cash: 'Cash:',
        phoneNumbers: 'Phone:',
        itemCash: 'Cash in Items:',
        blackMoney: 'Black Money:',
        loadingDetails: 'Loading details...',
        characters: 'Characters ({count} total)',
        inventory: 'Inventory ({count} items)',
        noItems: 'No items in inventory',
        ownedVehicles: 'Owned Vehicles ({count})',
        unknownVehicle: 'Unknown Vehicle',
        noPlate: 'NO PLATE',
        stored: 'Stored',
        out: 'Out',
        noVehicles: 'No vehicles owned',
        lookupVehicle: 'Lookup in Vehicle tab',
        characterDetails: 'Character Details',
        characterSlot: 'Character Slot:',
        dob: 'Date of Birth:',
        gender: 'Gender:',
        male: 'Male',
        female: 'Female',
        height: 'Height:',
        na: 'N/A',
        noResults: 'No players found matching "{search}"'
      };

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;

    setLoading(true);
    setError(null);
    setExpandedPlayer(null);
    setExpandedIdentifier(null);
    setDetailsError('');

    try {
      const response = await apiFetch(`/api/lookup/player?search=${encodeURIComponent(search)}`);
      if (!response.ok) throw new Error(t.failedSearch);
      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = async (player) => {
    if (expandedIdentifier === player.identifier) {
      setExpandedPlayer(null);
      setExpandedIdentifier(null);
      setDetailsError('');
    } else {
      setDetailsLoading(true);
      setExpandedIdentifier(player.identifier);
      setDetailsError('');
      // Prime expanded state immediately to avoid null render races.
      setExpandedPlayer({
        ...player,
        allCharacters: [],
        selectedCharIndex: 0,
        inventory: [],
        vehicles: []
      });
      try {
        // Fetch all characters for this player
        const response = await apiFetch(`/api/player/characters/${encodeURIComponent(player.identifier)}`);
        if (!response.ok) throw new Error(t.failedCharacters);
        const characters = await response.json();
        
        // Fetch details for first character
        if (characters.length > 0) {
          const detailsResponse = await apiFetch(`/api/player/details/${encodeURIComponent(characters[0].identifier)}`);
          if (!detailsResponse.ok) throw new Error(t.failedDetails);
          const details = await detailsResponse.json();
          
          setExpandedPlayer({
            ...details,
            allCharacters: characters,
            selectedCharIndex: 0
          });
        } else {
          setExpandedPlayer({
            ...player,
            allCharacters: [],
            selectedCharIndex: 0,
            inventory: [],
            vehicles: []
          });
          setDetailsError(t.noCharactersFound);
        }
      } catch (err) {
        console.error('Error fetching details:', err);
        setDetailsError(err.message || t.failedDetails);
        setExpandedPlayer({
          ...player,
          allCharacters: [],
          selectedCharIndex: 0,
          inventory: [],
          vehicles: []
        });
      } finally {
        setDetailsLoading(false);
      }
    }
  };

  const selectCharacter = async (charIndex) => {
    if (!expandedPlayer?.allCharacters) return;
    
    // Don't refetch if already selected
    if (expandedPlayer.selectedCharIndex === charIndex) return;
    
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const character = expandedPlayer.allCharacters[charIndex];
      const response = await apiFetch(`/api/player/details/${encodeURIComponent(character.identifier)}`);
      if (!response.ok) throw new Error(t.failedDetails);
      const details = await response.json();
      
      setExpandedPlayer({
        ...details,
        allCharacters: expandedPlayer.allCharacters,
        selectedCharIndex: charIndex
      });
    } catch (err) {
      console.error('Error fetching character:', err);
      setDetailsError(err.message || t.failedDetails);
    } finally {
      setDetailsLoading(false);
    }
  };

  const getCharacterNumber = (identifier) => {
    const match = identifier.match(/^char(\d+):/);
    return match ? `Char ${match[1]}` : identifier;
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const isStored = (vehicle) => {
    if (typeof vehicle?.isStored === 'boolean') return vehicle.isStored;
    return Number(vehicle?.stored) === 0;
  };

  const handleLookupPhone = (phoneNumber) => {
    const normalizedPhoneNumber = String(phoneNumber || '').trim();
    if (!normalizedPhoneNumber) return;
    if (onLookupPhone) onLookupPhone(normalizedPhoneNumber);
  };

  return (
    <div className="player-lookup">
      <div className="lookup-header">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      <form onSubmit={handleSearch} className="search-form">
        <input
          type="text"
          placeholder={t.placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <button type="submit" className="search-button" disabled={loading}>
          {loading ? t.searching : t.search}
        </button>
      </form>

      {error && <div className="error-message">{error}</div>}

      {results.length > 0 && (
        <div className="results-container">
          <h3>{text(t.foundPlayers, { count: results.length })}</h3>
          {results.map((player, index) => (
            <div key={index} className="player-result-card">
              <div className="player-result-header">
                <h4>{player.firstname} {player.lastname}</h4>
                <div className="header-actions">
                  {player.phoneNumbers && player.phoneNumbers.length > 0 && (
                    <div className="phone-badge-list">
                      {player.phoneNumbers.map((phoneNumber) => (
                        <button
                          type="button"
                          key={phoneNumber}
                          className="identifier-badge phone-badge phone-badge-button"
                          onClick={() => handleLookupPhone(phoneNumber)}
                        >
                          {phoneNumber}
                        </button>
                      ))}
                    </div>
                  )}
                  <span className="identifier-badge">{player.identifier}</span>
                  <button 
                    type="button"
                    className="view-details-btn"
                    onClick={() => toggleDetails(player)}
                    disabled={detailsLoading}
                  >
                    {expandedIdentifier === player.identifier ? `▲ ${t.hideDetails}` : `▼ ${t.viewDetails}`}
                  </button>
                </div>
              </div>
              
              <div className="player-result-body">
                <div className="info-row">
                  <span className="label">{t.job}</span>
                  <span className="value">{player.job} (Grade {player.job_grade})</span>
                </div>
                <div className="info-row">
                  <span className="label">{t.group}</span>
                  <span className="value">{player.group}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t.bank}</span>
                  <span className="value">{formatMoney(player.accounts?.bank || 0)}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t.cash}</span>
                  <span className="value">{formatMoney(player.accounts?.money || 0)}</span>
                </div>
                <div className="info-row">
                  <span className="label">{t.phoneNumbers}</span>
                  <span className="value">
                    {player.phoneNumbers && player.phoneNumbers.length > 0 ? (
                      <span className="phone-badge-list">
                        {player.phoneNumbers.map((phoneNumber) => (
                          <button
                            type="button"
                            key={phoneNumber}
                            className="identifier-badge phone-badge phone-badge-button"
                            onClick={() => handleLookupPhone(phoneNumber)}
                          >
                            {phoneNumber}
                          </button>
                        ))}
                      </span>
                    ) : t.na}
                  </span>
                </div>
                {player.itemCash > 0 && (
                  <div className="info-row">
                    <span className="label">{t.itemCash}</span>
                    <span className="value" title="Cash items stored in inventory">{formatMoney(player.itemCash)}</span>
                  </div>
                )}
                {player.accounts?.black_money > 0 && (
                  <div className="info-row">
                    <span className="label">{t.blackMoney}</span>
                    <span className="value">{formatMoney(player.accounts.black_money)}</span>
                  </div>
                )}
              </div>

              {/* Expanded Details Section */}
              {expandedIdentifier === player.identifier && (
                <div className="expanded-details">
                  {detailsLoading ? (
                    <div className="loading-details">{t.loadingDetails}</div>
                  ) : expandedPlayer ? (
                    <>
                      {detailsError && <div className="error-message">{detailsError}</div>}

                      {/* Character Selection */}
                      {expandedPlayer.allCharacters && expandedPlayer.allCharacters.length > 1 && (
                        <div className="character-selector-lookup">
                          <h4>{text(t.characters, { count: expandedPlayer.allCharacters.length })}</h4>
                          <div className="character-buttons-lookup">
                            {expandedPlayer.allCharacters.map((char, idx) => (
                              <button
                                type="button"
                                key={idx}
                                className={`character-btn-lookup ${expandedPlayer.selectedCharIndex === idx ? 'active' : ''}`}
                                onClick={() => selectCharacter(idx)}
                              >
                                <span className="char-number">{getCharacterNumber(char.identifier)}</span>
                                <span className="char-name">{char.firstname} {char.lastname}</span>
                                <span className="char-money">{formatMoney(char.accounts?.bank || 0)}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Inventory Section */}
                      <div className="details-section">
                        <h4>{text(t.inventory, { count: expandedPlayer.inventory?.length || 0 })}</h4>
                        {expandedPlayer.inventory && expandedPlayer.inventory.length > 0 ? (
                          <div className="inventory-grid-lookup">
                            {expandedPlayer.inventory.map((item, idx) => (
                              <div key={idx} className="inventory-item-lookup">
                                <span className="item-name">{item.name}</span>
                                <span className="item-count">x{item.count || item.amount || 1}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-items">{t.noItems}</div>
                        )}
                      </div>

                      {/* Vehicles Section */}
                      <div className="details-section">
                        <h4>{text(t.ownedVehicles, { count: expandedPlayer.vehicles?.length || 0 })}</h4>
                        {expandedPlayer.vehicles && expandedPlayer.vehicles.length > 0 ? (
                          <div className="vehicles-list-lookup">
                            {expandedPlayer.vehicles.map((vehicle, idx) => (
                              <div key={idx} className="vehicle-card-lookup">
                                <div className="vehicle-header-lookup">
                                  <span className="vehicle-model">{vehicle.model || t.unknownVehicle}</span>
                                  <span className="vehicle-plate">{vehicle.plate || t.noPlate}</span>
                                </div>
                                <div className="vehicle-stats">
                                  <span className={`vehicle-status ${isStored(vehicle) ? 'stored' : 'out'}`}>
                                    {isStored(vehicle) ? `${t.stored}` : `${t.out}`}
                                  </span>
                                  <span>F: {Math.round(vehicle.fuel) || 0}%</span>
                                  <span>G: {vehicle.garage_id || t.na}</span>
                                  <span>M: {Math.round(vehicle.mileage || 0)} km</span>
                                  {vehicle.engineHealth && <span>E: {vehicle.engineHealth}%</span>}
                                  {vehicle.bodyHealth && <span>B: {vehicle.bodyHealth}%</span>}
                                </div>
                                <div className="vehicle-lookup-actions">
                                  <button
                                    type="button"
                                    className="lookup-vehicle-btn"
                                    onClick={() => onLookupVehicle && onLookupVehicle(vehicle.plate)}
                                    disabled={!vehicle.plate}
                                  >
                                    {t.lookupVehicle}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-items">{t.noVehicles}</div>
                        )}
                      </div>

                      {/* Character Details */}
                      <div className="details-section">
                        <h4>{t.characterDetails}</h4>
                        <div className="character-info-grid">
                          <div className="char-info-item">
                            <span className="label">{t.characterSlot}</span>
                            <span className="value">{getCharacterNumber(expandedPlayer.identifier)}</span>
                          </div>
                          <div className="char-info-item">
                            <span className="label">{t.phoneNumbers}</span>
                            <span className="value">
                              {expandedPlayer.phoneNumbers && expandedPlayer.phoneNumbers.length > 0 ? (
                                <span className="phone-badge-list">
                                  {expandedPlayer.phoneNumbers.map((phoneNumber) => (
                                    <button
                                      type="button"
                                      key={phoneNumber}
                                      className="identifier-badge phone-badge phone-badge-button"
                                      onClick={() => handleLookupPhone(phoneNumber)}
                                    >
                                      {phoneNumber}
                                    </button>
                                  ))}
                                </span>
                              ) : t.na}
                            </span>
                          </div>
                          <div className="char-info-item">
                            <span className="label">{t.dob}</span>
                            <span className="value">{expandedPlayer.dateofbirth || t.na}</span>
                          </div>
                          <div className="char-info-item">
                            <span className="label">{t.gender}</span>
                            <span className="value">{expandedPlayer.sex === 'm' ? t.male : expandedPlayer.sex === 'f' ? t.female : t.na}</span>
                          </div>
                          <div className="char-info-item">
                            <span className="label">{t.height}</span>
                            <span className="value">{expandedPlayer.height || t.na}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="loading-details">{t.loadingDetails}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && results.length === 0 && search && !error && (
        <div className="no-results">{text(t.noResults, { search })}</div>
      )}
    </div>
  );
}

export default PlayerLookup;



